import { Text, Title } from "@clipboard-health/ui-components";
import { Checkbox, DialogContent, FormControlLabel, Stack, TextField } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useFormContext } from "react-hook-form";

import { type PostShiftReviewFormValues, type StepAction } from "../types";

const MIN_REVIEW_LENGTH = 10;
const MAX_REVIEW_LENGTH = 300;

export interface WriteReviewStepProps {
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function WriteReviewStep(props: WriteReviewStepProps) {
  const { isSubmitting = false, onAction } = props;
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();
  const reviewText = watch("reviewText");
  const isAnonymous = watch("isAnonymous");

  const isValid = reviewText.length >= MIN_REVIEW_LENGTH && reviewText.length <= MAX_REVIEW_LENGTH;
  const isOverLimit = reviewText.length > MAX_REVIEW_LENGTH;

  function getHelperText() {
    if (isOverLimit) {
      return `${reviewText.length}/${MAX_REVIEW_LENGTH} characters (too long)`;
    }

    if (reviewText.length < MIN_REVIEW_LENGTH) {
      return `${reviewText.length}/${MAX_REVIEW_LENGTH} characters (${
        MIN_REVIEW_LENGTH - reviewText.length
      } more needed)`;
    }

    return `${reviewText.length}/${MAX_REVIEW_LENGTH} characters`;
  }

  return (
    <>
      <DialogContent sx={{ py: 2, display: "flex", flexDirection: "column", gap: 6 }}>
        <Stack spacing={3}>
          <Title variant="h3" component="h3" textAlign="center">
            Leave your thoughts
          </Title>
        </Stack>

        <Stack spacing={4}>
          <TextField
            multiline
            rows={8}
            placeholder="Please review the shift and the workplace. What should a new worker at this workplace know?"
            value={reviewText}
            disabled={isSubmitting}
            error={isOverLimit}
            helperText={getHelperText()}
            sx={{ pt: 5 }}
            onChange={(event) => {
              setValue("reviewText", event.target.value, { shouldValidate: true });
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isAnonymous ?? false}
                disabled={isSubmitting}
                onChange={(event) => {
                  setValue("isAnonymous", event.target.checked, { shouldValidate: true });
                }}
              />
            }
            label={<Text>I want my review to be anonymous</Text>}
          />
        </Stack>

        <Text variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
          Reviews that use inappropriate language or certain references to patients and specific
          facility personnel will be removed. Repeat offenders may be banned from the marketplace
        </Text>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          isLoading={isSubmitting}
          disabled={!isValid}
          onClick={() => {
            onAction({
              type: "NEXT_STEP",
              data: { reviewText },
            });
          }}
        >
          Continue
        </LoadingButton>
      </DialogFooter>
    </>
  );
}
